import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { CheckCircle, XCircle, Play, Square, Pencil } from "lucide-react";

const STATUS_STYLE = {
  upcoming: "text-yellow-400 border-yellow-400/40",
  live: "text-gold border-gold",
  ended: "text-white/50 border-white/30",
};

const AuctionApproval = () => {
  const [auctions, setAuctions] = useState([]);
  const [tab, setTab] = useState("upcoming");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = () => api.get("/auctions").then(({ data }) => setAuctions(data));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    const label = { live: "approve & go LIVE", ended: "end this auction" }[status];
    if (!window.confirm(`Are you sure you want to ${label}?`)) return;
    try {
      await api.patch(`/auctions/${id}`, { status });
      toast.success(`Auction → ${status}.`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
  };

  const saveEdit = async () => {
    try {
      const payload = {};
      ["title", "description", "starts_at", "ends_at"].forEach((k) => { if (form[k]) payload[k] = form[k]; });
      ["reserve_price", "starting_bid", "bid_increment"].forEach((k) => { if (form[k]) payload[k] = parseFloat(form[k]); });
      await api.patch(`/auctions/${editing}`, payload);
      toast.success("Auction updated.");
      setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
  };

  const filtered = auctions.filter((a) => a.status === tab);

  return (
    <div data-testid="auction-approval" className="pb-32">
      <div className="flex gap-3 border-b border-white/10 pb-6 mb-8">
        {["upcoming", "live", "ended"].map((s) => (
          <button key={s} data-testid={`auc-tab-${s}`} onClick={() => setTab(s)}
            className={`font-accent text-[11px] tracking-[0.28em] uppercase px-5 py-3 transition-colors ${tab === s ? "text-gold border-b border-gold -mb-[25px] pb-6" : "text-white/50 hover:text-white"}`}>
            {s} <span className="ml-2 text-white/30">({auctions.filter((a) => a.status === s).length})</span>
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {filtered.map((a) => (
          <div key={a.id} data-testid={`auc-row-${a.id}`} className="bg-onyx border border-white/10 p-6">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
              <div>
                <span className={`font-accent text-[10px] tracking-[0.24em] uppercase px-3 py-1 border ${STATUS_STYLE[a.status]}`}>{a.status}</span>
                <span className="ml-3 text-white/40 text-xs">Ends {new Date(a.ends_at).toLocaleString()}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {a.status === "upcoming" && (
                  <button data-testid={`auc-approve-${a.id}`} onClick={() => setStatus(a.id, "live")}
                    className="bg-gold text-black font-accent uppercase tracking-[0.24em] text-[11px] px-4 py-2 hover:bg-ivory flex items-center gap-2">
                    <Play size={12} /> Approve & Go Live
                  </button>
                )}
                {a.status === "live" && (
                  <button data-testid={`auc-end-${a.id}`} onClick={() => setStatus(a.id, "ended")}
                    className="border border-white/30 text-white font-accent uppercase tracking-[0.24em] text-[11px] px-4 py-2 hover:border-red-400 hover:text-red-400 flex items-center gap-2">
                    <Square size={12} /> End Auction
                  </button>
                )}
                <button data-testid={`auc-edit-${a.id}`} onClick={() => { setEditing(a.id); setForm({ title: a.title, description: a.description, reserve_price: a.reserve_price, starting_bid: a.starting_bid, bid_increment: a.bid_increment, starts_at: a.starts_at, ends_at: a.ends_at }); }}
                  className="border border-white/30 text-white/70 font-accent uppercase tracking-[0.24em] text-[11px] px-4 py-2 hover:border-gold hover:text-gold flex items-center gap-2">
                  <Pencil size={12} /> Edit
                </button>
              </div>
            </div>
            <div className="font-heading text-2xl mb-2">{a.title}</div>
            <div className="text-white/60 text-sm mb-4">{a.gemstone?.name} · {a.gemstone?.carat} ct · {a.gemstone?.origin}</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm border-t border-white/10 pt-4">
              <Spec label="Starting" v={`$${Intl.NumberFormat("en-US").format(a.starting_bid)}`} />
              <Spec label="Reserve" v={`$${Intl.NumberFormat("en-US").format(a.reserve_price)}`} />
              <Spec label="Current Bid" v={`$${Intl.NumberFormat("en-US").format(a.current_bid)}`} />
              <Spec label="Bids" v={a.bid_count} />
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="text-white/40 text-center py-12">No {tab} auctions.</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="max-w-xl mx-auto my-12 bg-noir border border-gold/40 p-8" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading text-3xl mb-6">Edit Auction</h2>
            <div className="space-y-4">
              <Inp label="Title" v={form.title} on={(v) => setForm({...form, title: v})} testid="auc-f-title" />
              <Inp label="Description" v={form.description} on={(v) => setForm({...form, description: v})} testarea testid="auc-f-desc" />
              <div className="grid grid-cols-3 gap-3">
                <Inp label="Starting Bid (USD)" type="number" v={form.starting_bid} on={(v) => setForm({...form, starting_bid: v})} testid="auc-f-start" />
                <Inp label="Reserve (USD)" type="number" v={form.reserve_price} on={(v) => setForm({...form, reserve_price: v})} testid="auc-f-reserve" />
                <Inp label="Increment (USD)" type="number" v={form.bid_increment} on={(v) => setForm({...form, bid_increment: v})} testid="auc-f-inc" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Inp label="Starts At (ISO)" v={form.starts_at} on={(v) => setForm({...form, starts_at: v})} testid="auc-f-startat" />
                <Inp label="Ends At (ISO)" v={form.ends_at} on={(v) => setForm({...form, ends_at: v})} testid="auc-f-endat" />
              </div>
            </div>
            <div className="flex gap-3 mt-8">
              <button data-testid="auc-save-btn" onClick={saveEdit} className="bg-gold text-black font-accent uppercase tracking-[0.24em] text-xs px-7 py-3 hover:bg-ivory">Save</button>
              <button onClick={() => setEditing(null)} className="border border-white/30 text-white font-accent uppercase tracking-[0.24em] text-xs px-7 py-3 hover:border-white">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Spec = ({ label, v }) => (
  <div>
    <div className="text-white/40 text-xs uppercase tracking-[0.18em] font-accent mb-1">{label}</div>
    <div className="text-ivory font-heading text-xl">{v}</div>
  </div>
);

const Inp = ({ label, v, on, type = "text", testarea, testid }) => (
  <div>
    <label className="text-white/50 text-xs tracking-[0.18em] font-accent uppercase mb-2 block">{label}</label>
    {testarea ? (
      <textarea data-testid={testid} value={v || ""} onChange={(e) => on(e.target.value)} rows={3} className="w-full bg-transparent border border-white/20 px-3 py-3 text-white focus:outline-none focus:border-gold" />
    ) : (
      <input data-testid={testid} type={type} value={v || ""} onChange={(e) => on(e.target.value)} className="w-full bg-transparent border border-white/20 px-3 py-3 text-white focus:outline-none focus:border-gold" />
    )}
  </div>
);

export default AuctionApproval;
